import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import {
    Search, Globe, Link2, BarChart3, Target, ArrowRight,
    Loader2, CheckCircle, XCircle, AlertTriangle, ExternalLink,
    TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight,
    FileWarning, Image, Heading1, Shield, RefreshCw, Lock
} from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── SITE CRAWL TAB ───
function SiteCrawlTool() {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const runCrawl = async (e) => {
        e.preventDefault();
        if (!url.trim()) return toast.error("Enter a URL");
        setLoading(true);
        setResult(null);
        try {
            const { data } = await axios.post(`${API}/site-crawl`, { url: url.trim() });
            setResult(data);
            toast.success("Site crawl complete!");
        } catch {
            toast.error("Site crawl failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const gradeColor = (grade) => {
        if (grade === 'A+' || grade === 'A') return 'text-emerald-400';
        if (grade === 'B') return 'text-cyan-400';
        if (grade === 'C') return 'text-amber-400';
        return 'text-red-400';
    };

    return (
        <div>
            <form onSubmit={runCrawl} className="flex gap-3 mb-8" data-testid="crawl-form">
                <div className="flex-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                    <Globe className="h-4 w-4 text-gray-500 shrink-0" />
                    <Input type="text" placeholder="Enter website URL (e.g. exploremhc.com)" value={url} onChange={(e) => setUrl(e.target.value)}
                        className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto" data-testid="crawl-url-input" />
                </div>
                <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 rounded-lg" data-testid="crawl-submit">
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Crawling...</> : <><Search className="h-4 w-4 mr-2" />Crawl Site</>}
                </Button>
            </form>

            {loading && (
                <Card className="bg-white/5 border-white/10 mb-6">
                    <CardContent className="p-8 text-center">
                        <Loader2 className="h-10 w-10 text-emerald-400 animate-spin mx-auto mb-4" />
                        <p className="text-white font-medium">Crawling pages and checking technical SEO...</p>
                        <p className="text-gray-500 text-sm mt-1">This may take 10-20 seconds</p>
                    </CardContent>
                </Card>
            )}

            {result && (
                <div className="space-y-6" data-testid="crawl-results">
                    {/* Score Overview */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <Card className="bg-white/5 border-white/10 col-span-2 md:col-span-1">
                            <CardContent className="p-4 text-center">
                                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Technical Score</p>
                                <p className={`text-3xl font-bold ${gradeColor(result.grade)}`}>{result.score}</p>
                                <p className={`text-lg font-bold ${gradeColor(result.grade)}`}>{result.grade}</p>
                            </CardContent>
                        </Card>
                        {[
                            { label: "Pages Crawled", value: result.pages_crawled, icon: Globe },
                            { label: "Broken Links", value: result.summary.broken_links_count, icon: XCircle, bad: true },
                            { label: "Meta Issues", value: result.summary.meta_issues_count, icon: FileWarning, bad: true },
                            { label: "Missing Alt Text", value: result.summary.images_missing_alt_count, icon: Image, bad: true }
                        ].map((stat, i) => (
                            <Card key={i} className="bg-white/5 border-white/10">
                                <CardContent className="p-4 text-center">
                                    <stat.icon className={`h-5 w-5 mx-auto mb-1 ${stat.bad && stat.value > 0 ? 'text-red-400' : 'text-gray-400'}`} />
                                    <p className="text-gray-500 text-xs uppercase tracking-wider">{stat.label}</p>
                                    <p className={`text-2xl font-bold ${stat.bad && stat.value > 0 ? 'text-red-400' : 'text-white'}`}>{stat.value}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* HTTPS Check */}
                    <Card className={`border ${result.uses_https ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <Shield className={`h-5 w-5 ${result.uses_https ? 'text-emerald-400' : 'text-red-400'}`} />
                            <span className="text-white font-medium">{result.uses_https ? 'HTTPS enabled — site is secure' : 'No HTTPS detected — site is NOT secure'}</span>
                        </CardContent>
                    </Card>

                    {/* Meta Issues */}
                    {result.meta_issues.length > 0 && (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-5">
                                <h3 className="text-white font-bold mb-3 flex items-center gap-2"><FileWarning className="h-5 w-5 text-amber-400" />Meta Tag Issues</h3>
                                <div className="space-y-2">
                                    {result.meta_issues.map((issue, i) => (
                                        <div key={i} className="flex items-start gap-2 text-sm">
                                            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                                            <div>
                                                <span className="text-gray-300">{issue.issue}</span>
                                                <span className="text-gray-600 text-xs ml-2">{issue.url}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Heading Issues */}
                    {result.heading_issues.length > 0 && (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-5">
                                <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Heading1 className="h-5 w-5 text-violet-400" />Heading Issues</h3>
                                <div className="space-y-2">
                                    {result.heading_issues.map((issue, i) => (
                                        <div key={i} className="flex items-start gap-2 text-sm">
                                            <AlertTriangle className="h-4 w-4 text-violet-400 mt-0.5 shrink-0" />
                                            <div>
                                                <span className="text-gray-300">{issue.issue}</span>
                                                <span className="text-gray-600 text-xs ml-2">{issue.url}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Pages Crawled */}
                    <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-5">
                            <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Globe className="h-5 w-5 text-cyan-400" />Pages Crawled</h3>
                            <div className="space-y-2">
                                {result.pages.map((page, i) => (
                                    <div key={i} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white text-sm truncate">{page.url}</p>
                                            <div className="flex gap-3 text-xs text-gray-500 mt-1">
                                                <span>{page.word_count} words</span>
                                                <span>{page.img_count} images</span>
                                                {page.img_missing_alt > 0 && <span className="text-amber-400">{page.img_missing_alt} missing alt</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {page.issues.length > 0 ? (
                                                <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-1 rounded">{page.issues.length} issues</span>
                                            ) : (
                                                <span className="text-xs text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded">Clean</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}

// ─── BACKLINK CHECKER TAB ───
function BacklinkTool() {
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const checkBacklinks = async (e) => {
        e.preventDefault();
        if (!url.trim()) return toast.error("Enter a URL");
        setLoading(true);
        setResult(null);
        try {
            const { data } = await axios.post(`${API}/backlinks`, { url: url.trim() });
            if (data.error) {
                toast.error("Analysis temporarily unavailable. Please try again later.");
            } else {
                setResult(data);
                toast.success("Backlink analysis complete!");
            }
        } catch {
            toast.error("Backlink analysis failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <form onSubmit={checkBacklinks} className="flex gap-3 mb-8" data-testid="backlink-form">
                <div className="flex-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                    <Link2 className="h-4 w-4 text-gray-500 shrink-0" />
                    <Input type="text" placeholder="Enter website URL (e.g. exploremhc.com)" value={url} onChange={(e) => setUrl(e.target.value)}
                        className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto" data-testid="backlink-url-input" />
                </div>
                <Button type="submit" disabled={loading} className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 rounded-lg" data-testid="backlink-submit">
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing...</> : <><Link2 className="h-4 w-4 mr-2" />Check Backlinks</>}
                </Button>
            </form>

            {loading && (
                <Card className="bg-white/5 border-white/10 mb-6">
                    <CardContent className="p-8 text-center">
                        <Loader2 className="h-10 w-10 text-blue-400 animate-spin mx-auto mb-4" />
                        <p className="text-white font-medium">Analyzing backlink profile...</p>
                        <p className="text-gray-500 text-sm mt-1">AI-powered analysis may take 15-30 seconds</p>
                    </CardContent>
                </Card>
            )}

            {result && (
                <div className="space-y-6" data-testid="backlink-results">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { label: "Est. Backlinks", value: result.estimated_backlinks?.toLocaleString(), color: "text-blue-400" },
                            { label: "Referring Domains", value: result.estimated_referring_domains?.toLocaleString(), color: "text-cyan-400" },
                            { label: "Domain Authority", value: `${result.domain_authority_estimate}/100`, color: "text-violet-400" },
                            { label: "Growth Trend", value: result.growth_trend, color: result.growth_trend === 'growing' ? 'text-emerald-400' : result.growth_trend === 'declining' ? 'text-red-400' : 'text-gray-400' }
                        ].map((stat, i) => (
                            <Card key={i} className="bg-white/5 border-white/10">
                                <CardContent className="p-4 text-center">
                                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{stat.label}</p>
                                    <p className={`text-2xl font-bold ${stat.color} capitalize`}>{stat.value}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Quality Breakdown */}
                    {result.backlink_quality_breakdown && (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-5">
                                <h3 className="text-white font-bold mb-3">Link Quality</h3>
                                <div className="flex gap-2 h-4 rounded-full overflow-hidden mb-3">
                                    <div style={{ width: `${result.backlink_quality_breakdown.high_quality}%` }} className="bg-emerald-500" />
                                    <div style={{ width: `${result.backlink_quality_breakdown.medium_quality}%` }} className="bg-amber-500" />
                                    <div style={{ width: `${result.backlink_quality_breakdown.low_quality}%` }} className="bg-red-500" />
                                </div>
                                <div className="flex gap-6 text-xs">
                                    <span className="text-emerald-400">High: {result.backlink_quality_breakdown.high_quality}%</span>
                                    <span className="text-amber-400">Medium: {result.backlink_quality_breakdown.medium_quality}%</span>
                                    <span className="text-red-400">Low: {result.backlink_quality_breakdown.low_quality}%</span>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Top Referring Domains */}
                    {result.top_referring_domains?.length > 0 && (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-5">
                                <h3 className="text-white font-bold mb-3">Top Referring Domains</h3>
                                <div className="space-y-2">
                                    {result.top_referring_domains.map((d, i) => (
                                        <div key={i} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                                            <div>
                                                <p className="text-white text-sm font-medium">{d.domain}</p>
                                                <p className="text-gray-500 text-xs">{d.context}</p>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs">
                                                <span className="text-gray-400">DA: {d.authority}</span>
                                                <span className={d.link_type === 'dofollow' ? 'text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded' : 'text-gray-500 bg-white/5 px-2 py-0.5 rounded'}>{d.link_type}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Opportunities */}
                    {result.opportunities?.length > 0 && (
                        <Card className="bg-emerald-500/10 border-emerald-500/30">
                            <CardContent className="p-5">
                                <h3 className="text-white font-bold mb-3 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-400" />Opportunities</h3>
                                <ul className="space-y-2">
                                    {result.opportunities.map((opp, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                                            <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                                            {opp}
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── KEYWORD TRACKER TAB ───
function KeywordTool() {
    const [url, setUrl] = useState('');
    const [keywords, setKeywords] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const analyzeKeywords = async (e) => {
        e.preventDefault();
        if (!url.trim()) return toast.error("Enter a URL");
        setLoading(true);
        setResult(null);
        try {
            const kwList = keywords.trim() ? keywords.split(',').map(k => k.trim()).filter(Boolean) : null;
            const { data } = await axios.post(`${API}/keywords`, { url: url.trim(), keywords: kwList });
            if (data.error) {
                toast.error("Analysis temporarily unavailable. Please try again later.");
            } else {
                setResult(data);
                toast.success("Keyword analysis complete!");
            }
        } catch {
            toast.error("Keyword analysis failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const posColor = (pos) => {
        if (pos <= 3) return 'text-emerald-400';
        if (pos <= 10) return 'text-cyan-400';
        if (pos <= 20) return 'text-amber-400';
        return 'text-red-400';
    };

    const trendIcon = (trend) => {
        if (trend === 'up') return <ArrowUpRight className="h-3 w-3 text-emerald-400" />;
        if (trend === 'down') return <ArrowDownRight className="h-3 w-3 text-red-400" />;
        return <Minus className="h-3 w-3 text-gray-500" />;
    };

    return (
        <div>
            <form onSubmit={analyzeKeywords} className="space-y-3 mb-8" data-testid="keyword-form">
                <div className="flex gap-3">
                    <div className="flex-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                        <Globe className="h-4 w-4 text-gray-500 shrink-0" />
                        <Input type="text" placeholder="Enter website URL" value={url} onChange={(e) => setUrl(e.target.value)}
                            className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto" data-testid="keyword-url-input" />
                    </div>
                    <Button type="submit" disabled={loading} className="bg-violet-500 hover:bg-violet-600 text-white font-bold px-6 rounded-lg" data-testid="keyword-submit">
                        {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Analyzing...</> : <><BarChart3 className="h-4 w-4 mr-2" />Analyze</>}
                    </Button>
                </div>
                <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                    <Search className="h-4 w-4 text-gray-500 shrink-0" />
                    <Input type="text" placeholder="Track specific keywords (comma separated, optional)" value={keywords} onChange={(e) => setKeywords(e.target.value)}
                        className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto" data-testid="keyword-list-input" />
                </div>
            </form>

            {loading && (
                <Card className="bg-white/5 border-white/10 mb-6">
                    <CardContent className="p-8 text-center">
                        <Loader2 className="h-10 w-10 text-violet-400 animate-spin mx-auto mb-4" />
                        <p className="text-white font-medium">Analyzing keyword profile...</p>
                        <p className="text-gray-500 text-sm mt-1">AI-powered analysis may take 15-30 seconds</p>
                    </CardContent>
                </Card>
            )}

            {result && (
                <div className="space-y-6" data-testid="keyword-results">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { label: "Organic Keywords", value: result.estimated_organic_keywords?.toLocaleString(), color: "text-violet-400" },
                            { label: "Est. Monthly Traffic", value: result.estimated_monthly_traffic?.toLocaleString(), color: "text-cyan-400" },
                            { label: "Top 10 Keywords", value: result.keyword_distribution?.top_10, color: "text-emerald-400" },
                            { label: "Top 100 Keywords", value: result.keyword_distribution?.top_100, color: "text-blue-400" }
                        ].map((stat, i) => (
                            <Card key={i} className="bg-white/5 border-white/10">
                                <CardContent className="p-4 text-center">
                                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{stat.label}</p>
                                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value || 0}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Top Keywords Table */}
                    {result.top_ranking_keywords?.length > 0 && (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-5">
                                <h3 className="text-white font-bold mb-3">Top Ranking Keywords</h3>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-gray-500 text-xs uppercase border-b border-white/5">
                                                <th className="text-left py-2 pr-4">Keyword</th>
                                                <th className="text-center py-2 px-3">Position</th>
                                                <th className="text-center py-2 px-3">Volume</th>
                                                <th className="text-center py-2 px-3">Difficulty</th>
                                                <th className="text-center py-2 px-3">Trend</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.top_ranking_keywords.map((kw, i) => (
                                                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                                                    <td className="py-2 pr-4 text-white">{kw.keyword}</td>
                                                    <td className="py-2 px-3 text-center">
                                                        <span className={`font-bold ${posColor(kw.position)}`}>#{kw.position}</span>
                                                    </td>
                                                    <td className="py-2 px-3 text-center text-gray-400">{kw.volume?.toLocaleString()}</td>
                                                    <td className="py-2 px-3 text-center">
                                                        <span className={`${kw.difficulty <= 30 ? 'text-emerald-400' : kw.difficulty <= 60 ? 'text-amber-400' : 'text-red-400'}`}>{kw.difficulty}</span>
                                                    </td>
                                                    <td className="py-2 px-3 text-center">{trendIcon(kw.trend)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Opportunities */}
                    {result.top_opportunities?.length > 0 && (
                        <Card className="bg-violet-500/10 border-violet-500/30">
                            <CardContent className="p-5">
                                <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Target className="h-5 w-5 text-violet-400" />Quick Win Opportunities</h3>
                                <div className="space-y-3">
                                    {result.top_opportunities.map((opp, i) => (
                                        <div key={i} className="bg-white/5 rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-white font-medium text-sm">{opp.keyword}</span>
                                                <div className="flex items-center gap-2 text-xs">
                                                    <span className="text-amber-400">Pos #{opp.current_position}</span>
                                                    <span className="text-gray-500">Vol: {opp.volume}</span>
                                                </div>
                                            </div>
                                            <p className="text-gray-400 text-xs">{opp.potential}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Content Gaps */}
                    {result.content_gaps?.length > 0 && (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-5">
                                <h3 className="text-white font-bold mb-3">Content Gaps</h3>
                                <div className="space-y-2">
                                    {result.content_gaps.map((gap, i) => (
                                        <div key={i} className="flex items-start gap-2 text-sm">
                                            <ArrowRight className="h-4 w-4 text-cyan-400 mt-0.5 shrink-0" />
                                            <div>
                                                <span className="text-white">{gap.topic}</span>
                                                <span className="text-gray-500 ml-2 text-xs">~{gap.estimated_volume} searches/mo</span>
                                                <p className="text-gray-400 text-xs mt-0.5">{gap.recommendation}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── COMPETITOR COMPARISON TAB ───
function CompetitorTool() {
    const [url, setUrl] = useState('');
    const [compUrl, setCompUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const runComparison = async (e) => {
        e.preventDefault();
        if (!url.trim() || !compUrl.trim()) return toast.error("Enter both URLs");
        setLoading(true);
        setResult(null);
        try {
            const { data } = await axios.post(`${API}/competitor-compare`, { url: url.trim(), competitor_url: compUrl.trim() });
            setResult(data);
            toast.success("Comparison complete!");
        } catch {
            toast.error("Comparison failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const gradeColor = (grade) => {
        if (grade === 'A+' || grade === 'A') return 'text-emerald-400';
        if (grade === 'B') return 'text-cyan-400';
        if (grade === 'C') return 'text-amber-400';
        return 'text-red-400';
    };

    return (
        <div>
            <form onSubmit={runComparison} className="space-y-3 mb-8" data-testid="competitor-form">
                <div className="flex gap-3">
                    <div className="flex-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                        <Globe className="h-4 w-4 text-emerald-500 shrink-0" />
                        <Input type="text" placeholder="Your website URL" value={url} onChange={(e) => setUrl(e.target.value)}
                            className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto" data-testid="competitor-url-input" />
                    </div>
                    <span className="text-gray-500 self-center font-bold">VS</span>
                    <div className="flex-1 flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                        <Globe className="h-4 w-4 text-red-500 shrink-0" />
                        <Input type="text" placeholder="Competitor URL" value={compUrl} onChange={(e) => setCompUrl(e.target.value)}
                            className="bg-transparent border-0 text-white placeholder:text-gray-500 focus-visible:ring-0 p-0 h-auto" data-testid="competitor-comp-url-input" />
                    </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-bold py-6 rounded-lg text-base" data-testid="competitor-submit">
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Comparing...</> : <><Target className="h-5 w-5 mr-2" />Compare Now</>}
                </Button>
            </form>

            {loading && (
                <Card className="bg-white/5 border-white/10 mb-6">
                    <CardContent className="p-8 text-center">
                        <Loader2 className="h-10 w-10 text-cyan-400 animate-spin mx-auto mb-4" />
                        <p className="text-white font-medium">Running audits on both websites...</p>
                        <p className="text-gray-500 text-sm mt-1">Comparing side-by-side — this may take 20-40 seconds</p>
                    </CardContent>
                </Card>
            )}

            {result && (
                <div className="space-y-6" data-testid="competitor-results">
                    {/* Head-to-Head Score */}
                    <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-6">
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <p className="text-gray-500 text-xs mb-2 truncate">{result.your_site.url.replace(/https?:\/\//, '')}</p>
                                    <p className={`text-4xl font-bold ${gradeColor(result.your_site.overall_grade)}`}>{result.your_site.overall_score}</p>
                                    <p className={`text-lg font-bold ${gradeColor(result.your_site.overall_grade)}`}>{result.your_site.overall_grade}</p>
                                </div>
                                <div className="flex items-center justify-center">
                                    <span className="text-2xl font-bold text-gray-600">VS</span>
                                </div>
                                <div>
                                    <p className="text-gray-500 text-xs mb-2 truncate">{result.competitor_site.url.replace(/https?:\/\//, '')}</p>
                                    <p className={`text-4xl font-bold ${gradeColor(result.competitor_site.overall_grade)}`}>{result.competitor_site.overall_score}</p>
                                    <p className={`text-lg font-bold ${gradeColor(result.competitor_site.overall_grade)}`}>{result.competitor_site.overall_grade}</p>
                                </div>
                            </div>
                            <div className="text-center mt-4 pt-4 border-t border-white/5">
                                <p className="text-sm">
                                    <span className="text-emerald-400 font-bold">{result.winner.replace(/https?:\/\//, '')}</span>
                                    <span className="text-gray-500"> wins by </span>
                                    <span className="text-white font-bold">{result.score_difference} points</span>
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Category Comparison */}
                    <Card className="bg-white/5 border-white/10">
                        <CardContent className="p-5">
                            <h3 className="text-white font-bold mb-4">Category Breakdown</h3>
                            <div className="space-y-4">
                                {result.your_site.categories.map((cat, i) => {
                                    const compCat = result.competitor_site.categories[i];
                                    if (!compCat) return null;
                                    const youWin = cat.score >= compCat.score;
                                    return (
                                        <div key={i}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-gray-400 text-xs">{cat.name}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className={`text-sm font-bold w-8 text-right ${youWin ? 'text-emerald-400' : 'text-gray-400'}`}>{cat.score}</span>
                                                <div className="flex-1 flex gap-1 h-3">
                                                    <div className="flex-1 bg-white/5 rounded-l-full overflow-hidden flex justify-end">
                                                        <div style={{ width: `${cat.score}%` }} className={`${youWin ? 'bg-emerald-500' : 'bg-gray-500'} rounded-l-full`} />
                                                    </div>
                                                    <div className="flex-1 bg-white/5 rounded-r-full overflow-hidden">
                                                        <div style={{ width: `${compCat.score}%` }} className={`${!youWin ? 'bg-red-500' : 'bg-gray-500'} rounded-r-full`} />
                                                    </div>
                                                </div>
                                                <span className={`text-sm font-bold w-8 ${!youWin ? 'text-red-400' : 'text-gray-400'}`}>{compCat.score}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}

// ─── TABS ───
const TOOLS = [
    { id: 'crawl', label: 'Site Crawl', icon: Search, color: 'emerald', desc: 'Technical SEO audit — broken links, meta tags, headings' },
    { id: 'backlinks', label: 'Backlinks', icon: Link2, color: 'blue', desc: 'AI-powered backlink profile analysis' },
    { id: 'keywords', label: 'Keywords', icon: BarChart3, color: 'violet', desc: 'Keyword rankings, opportunities & content gaps' },
    { id: 'competitor', label: 'Competitor', icon: Target, color: 'cyan', desc: 'Head-to-head website comparison' }
];

const tabColors = {
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500',
    violet: 'bg-violet-500/20 text-violet-400 border-violet-500',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500'
};

export default function SEOToolsPage({ onRunAudit, onNavigate }) {
    const [activeTab, setActiveTab] = useState('crawl');
    const activeTool = TOOLS.find(t => t.id === activeTab);

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO
                title="SEO Tools Suite | SiteGradePro"
                description="Professional SEO tools: Site Crawler, Backlink Checker, Keyword Rank Tracker, and Competitor Comparison. Analyze any website for free."
                keywords="SEO tools, backlink checker, keyword tracker, site crawler, competitor analysis, website audit"
            />
            <Navbar activePage="seo-tools" onNavigate={onNavigate} onRunAudit={onRunAudit} />

            <section className="py-12 px-4">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">SEO Tools Suite</h1>
                        <p className="text-gray-400">Professional-grade SEO analysis — comparable to premium tools</p>
                    </div>

                    {/* Tool Tabs */}
                    <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="seo-tool-tabs">
                        {TOOLS.map(tool => {
                            const Icon = tool.icon;
                            const active = activeTab === tool.id;
                            return (
                                <button
                                    key={tool.id}
                                    onClick={() => setActiveTab(tool.id)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium whitespace-nowrap transition-all
                                        ${active ? tabColors[tool.color] : 'border-white/10 text-gray-400 hover:bg-white/5'}`}
                                    data-testid={`tab-${tool.id}`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {tool.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Tool Description */}
                    <p className="text-gray-500 text-sm mb-6">{activeTool?.desc}</p>

                    {/* Tool Content */}
                    {activeTab === 'crawl' && <SiteCrawlTool />}
                    {activeTab === 'backlinks' && <BacklinkTool />}
                    {activeTab === 'keywords' && <KeywordTool />}
                    {activeTab === 'competitor' && <CompetitorTool />}
                </div>
            </section>

            <Footer onNavigate={onNavigate} />
        </div>
    );
}
